import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { supabase, FavoriteCharacter, AnimeCharacter } from '../../lib/supabase';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SEO from '../../components/SEO';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

type FavoriteWithCharacter = FavoriteCharacter & { anime_characters: AnimeCharacter };

function SortableItem({
  favorite,
  onRemove,
}: {
  favorite: FavoriteWithCharacter;
  onRemove: (id: string) => void;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: favorite.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className="bg-white rounded-lg shadow-md p-3 md:p-4 flex items-center gap-2 md:gap-4"
    >
      <div
        {...attributes}
        {...listeners}
        className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-grab active:cursor-grabbing flex-shrink-0"
      >
        <i className="ri-draggable text-lg md:text-xl"></i>
      </div>

      <div className="flex-1 flex items-center gap-2 md:gap-4 min-w-0">
        <div className="w-12 h-16 md:w-16 md:h-20 rounded-md overflow-hidden flex-shrink-0">
          <img
            src={favorite.anime_characters.image_url}
            alt={favorite.anime_characters.character_name}
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-sm md:text-base text-gray-900 truncate">
            {favorite.anime_characters.character_name}
          </h3>
          <p className="text-xs md:text-sm text-gray-600 truncate">
            {favorite.anime_characters.anime_name}
          </p>
        </div>
        <div className="text-xs md:text-sm font-medium text-pink-500 flex-shrink-0">
          #{favorite.position}
        </div>
      </div>

      <button
        onClick={() => onRemove(favorite.id)}
        className="w-6 h-6 md:w-8 md:h-8 flex items-center justify-center text-red-400 hover:text-red-600 cursor-pointer flex-shrink-0"
      >
        <i className="ri-delete-bin-line text-lg md:text-xl"></i>
      </button>
    </div>
  );
}

export default function FavoritesPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [favorites, setFavorites] = useState<FavoriteWithCharacter[]>([]);
  const [allCharacters, setAllCharacters] = useState<AnimeCharacter[]>([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (!user) {
      navigate('/rate');
      return;
    }
    loadFavorites();
    loadAllCharacters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const loadFavorites = async () => {
    if (!user) return;

    const { data, error } = await supabase
      .from('favorite_characters')
      .select('*, anime_characters(*)')
      .eq('user_id', user.id)
      .order('position', { ascending: true });

    if (error) {
      console.error('Failed to load favorites:', error);
      return;
    }

    if (data) {
      setFavorites(data as any);
    }
    setLoading(false);
  };

  const loadAllCharacters = async () => {
    const { data, error } = await supabase
      .from('anime_characters')
      .select('*')
      .order('character_name', { ascending: true });

    if (error) {
      console.error('Failed to load characters:', error);
      return;
    }

    if (data) {
      setAllCharacters(data);
    }
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = favorites.findIndex((f) => f.id === active.id);
    const newIndex = favorites.findIndex((f) => f.id === over.id);

    const newFavorites = arrayMove(favorites, oldIndex, newIndex);
    
    // Update UI optimistically
    setFavorites(newFavorites);

    setSaving(true);
    try {
      // Strategy: Delete all and re-insert in correct order to avoid constraint issues
      const favoriteIds = newFavorites.map(f => f.id);
      const favoriteData = newFavorites.map((f, index) => ({
        id: f.id,
        user_id: f.user_id,
        character_id: f.character_id,
        position: index + 1,
        created_at: f.created_at
      }));

      // Delete all current favorites
      const { error: deleteError } = await supabase
        .from('favorite_characters')
        .delete()
        .in('id', favoriteIds);

      if (deleteError) {
        console.error('Error deleting favorites for reorder:', deleteError);
        throw deleteError;
      }

      // Re-insert with new positions
      const { error: insertError } = await supabase
        .from('favorite_characters')
        .insert(favoriteData);

      if (insertError) {
        console.error('Error re-inserting favorites:', insertError);
        throw insertError;
      }

      // Update local state with the new positions
      setFavorites(newFavorites.map((fav, index) => ({
        ...fav,
        position: index + 1,
      })));
      
    } catch (e) {
      console.error('Error updating positions after drag:', e);
      alert('Failed to save new order. Please try again.');
      await loadFavorites();
    } finally {
      setSaving(false);
    }
  };

  const handleRemove = async (id: string) => {
    if (!window.confirm('Remove this character from your favorites?')) return;

    setSaving(true);
    try {
      await supabase.from('favorite_characters').delete().eq('id', id);

      const newFavorites = favorites.filter((f) => f.id !== id);

      // Re‑order remaining items using 1-5 (database constraint)
      // Update sequentially to avoid conflicts
      for (let i = 0; i < newFavorites.length; i++) {
        await supabase
          .from('favorite_characters')
          .update({ position: i + 1 })
          .eq('id', newFavorites[i].id);
      }

      // Update local state with correct positions (1-5)
      setFavorites(
        newFavorites.map((fav, idx) => ({
          ...fav,
          position: idx + 1,
        }))
      );
    } catch (e) {
      console.error('Error removing favorite:', e);
      alert('Failed to remove character. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleAddFavorite = async (character: AnimeCharacter) => {
    if (!user) return;

    setSaving(true);
    try {
      // Refresh current favorites
      const { data: currentFavorites, error: fetchError } = await supabase
        .from('favorite_characters')
        .select('id, character_id, position')
        .eq('user_id', user.id)
        .order('position', { ascending: true });

      if (fetchError) {
        console.error('Error fetching favorites:', fetchError);
        alert('Failed to load favorites. Please refresh and try again.');
        setSaving(false);
        return;
      }

      if (currentFavorites && currentFavorites.length >= 5) {
        alert('You can only have 5 favorite characters. Remove one first.');
        setSaving(false);
        return;
      }

      const exists = currentFavorites?.some(
        (f) => f.character_id === character.id
      );
      if (exists) {
        alert('This character is already in your favorites!');
        setSaving(false);
        return;
      }

      // Fix any gaps in positions first (ensure sequential 1-5)
      if (currentFavorites && currentFavorites.length > 0) {
        const expectedPositions = currentFavorites.map((_, idx) => idx + 1);
        const actualPositions = currentFavorites.map(f => f.position);
        const hasGaps = !expectedPositions.every((pos, idx) => pos === actualPositions[idx]);
        
        if (hasGaps) {
          console.log('Fixing position gaps before adding new favorite...');
          const fixPromises = currentFavorites.map((f, idx) =>
            supabase
              .from('favorite_characters')
              .update({ position: idx + 1 })
              .eq('id', f.id)
          );
          await Promise.all(fixPromises);
          
          // Wait a moment for the database to update
          await new Promise(resolve => setTimeout(resolve, 500));
        }
      }

      // Calculate next position (must be between 1 and 5)
      const nextPosition = currentFavorites ? currentFavorites.length + 1 : 1;
      
      // Validate position is within allowed range (1-5)
      if (nextPosition < 1 || nextPosition > 5) {
        console.error('Invalid position calculated:', nextPosition);
        alert('Unable to add favorite. Please refresh the page and try again.');
        setSaving(false);
        return;
      }

      const { error: insertError } = await supabase
        .from('favorite_characters')
        .insert({
          user_id: user.id,
          character_id: character.id,
          position: nextPosition,
        });

      if (insertError) {
        console.error('Error adding favorite:', insertError);
        if (insertError.code === '23514') {
          alert('Position validation failed. Please refresh the page and try again.');
        } else if (insertError.code === '23505') {
          alert('This character is already in your favorites!');
        } else {
          alert('Failed to add character to favorites. Please try again.');
        }
        setSaving(false);
        return;
      }

      // Refresh UI
      await loadFavorites();
      setShowAddModal(false);
      setSearchQuery('');
    } catch (error) {
      console.error('Unexpected error adding favorite:', error);
      alert('Failed to add character to favorites. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const filteredCharacters = allCharacters.filter(
    (char) =>
      char.character_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      char.anime_name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50 flex items-center justify-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-pink-500 border-t-transparent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 to-purple-50">
      <SEO
        title="Manage Favorites - Anime HOT or NOT"
        description="Organize and manage your top 5 favorite anime characters"
        keywords="anime favorites, manage favorites, top characters"
        canonical="/favorites"
      />

      <Navbar />

      <div className="pt-20 md:pt-24 pb-12 md:pb-16 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="bg-white rounded-xl md:rounded-2xl shadow-xl p-4 md:p-8 mb-6 md:mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-gray-900 mb-2">
                  Manage Favorites
                </h1>
                <p className="text-xs md:text-sm text-gray-600">
                  Drag to reorder • Maximum 5 characters • {favorites.length}/5 slots used
                </p>
              </div>
              {favorites.length < 5 && (
                <button
                  onClick={() => setShowAddModal(true)}
                  className="px-5 py-2.5 md:px-6 md:py-3 text-sm md:text-base font-semibold text-white bg-gradient-to-r from-pink-500 to-purple-600 rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-add-line mr-2"></i>
                  Add Character
                </button>
              )}
            </div>

            {saving && (
              <div className="mb-4 p-3 bg-pink-50 border border-pink-200 rounded-lg text-xs md:text-sm text-pink-700">
                <i className="ri-loader-4-line animate-spin mr-2"></i>
                Saving order...
              </div>
            )}

            {favorites.length > 0 ? (
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={favorites.map((f) => f.id)}
                  strategy={verticalListSortingStrategy}
                >
                  <div className="space-y-2 md:space-y-3">
                    {favorites.map((favorite) => (
                      <SortableItem
                        key={favorite.id}
                        favorite={favorite}
                        onRemove={handleRemove}
                      />
                    ))}
                  </div>
                </SortableContext>
              </DndContext>
            ) : (
              <div className="text-center py-8 md:py-12">
                <i className="ri-heart-line text-5xl md:text-6xl text-gray-300 mb-4"></i>
                <p className="text-xs md:text-sm text-gray-500 mb-4">
                  You haven't added any favorite characters yet
                </p>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="px-5 py-2.5 md:px-6 md:py-3 text-sm md:text-base font-semibold text-white bg-gradient-to-r from-pink-500 to-purple-600 rounded-full hover:shadow-lg transition-all whitespace-nowrap cursor-pointer"
                >
                  Add Your First Favorite
                </button>
              </div>
            )}
          </div>

          <div className="text-center">
            <button
              onClick={() => navigate(`/profile/${user?.id}`)}
              className="text-xs md:text-sm font-medium text-pink-500 hover:text-pink-600 cursor-pointer whitespace-nowrap"
            >
              ← Back to Profile
            </button>
          </div>
        </div>
      </div>

      <Footer />

      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl md:rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
            <div className="p-4 md:p-6 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900">
                Add Favorite Character
              </h2>
              <button
                onClick={() => {
                  setShowAddModal(false);
                  setSearchQuery('');
                }}
                className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer"
              >
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>

            <div className="p-4 md:p-6 border-b border-gray-200 flex-shrink-0">
              <div className="relative">
                <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-base md:text-lg"></i>
                <input
                  type="text"
                  placeholder="Search characters or anime..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-11 md:pl-12 pr-4 py-2.5 md:py-3 border border-gray-300 rounded-full focus:ring-2 focus:ring-pink-500 focus:border-transparent text-sm"
                  autoFocus
                />
              </div>
            </div>

            <div className="flex-1 overflow-y-auto p-4 md:p-6">
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 md:gap-4">
                {filteredCharacters.map((character) => {
                  const isInFavorites = favorites.some(
                    (f) => f.character_id === character.id
                  );
                  return (
                    <div
                      key={character.id}
                      onClick={() =>
                        !isInFavorites && handleAddFavorite(character)
                      }
                      className={`group ${
                        isInFavorites
                          ? 'opacity-50 cursor-not-allowed'
                          : 'cursor-pointer hover:scale-105 transition-transform'
                      }`}
                    >
                      <div className="aspect-[3/4] rounded-lg overflow-hidden shadow-md group-hover:shadow-xl transition-shadow relative">
                        <img
                          src={character.image_url}
                          alt={character.character_name}
                          className="w-full h-full object-cover object-top"
                        />
                        {isInFavorites && (
                          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                            <i className="ri-check-line text-white text-3xl md:text-4xl"></i>
                          </div>
                        )}
                        {!isInFavorites && (
                          <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-end justify-center pb-3 md:pb-4">
                            <span className="text-white text-xs md:text-sm font-semibold">
                              Click to Add
                            </span>
                          </div>
                        )}
                      </div>
                      <p className="mt-1.5 md:mt-2 text-xs font-medium text-gray-700 text-center truncate">
                        {character.character_name}
                      </p>
                      <p className="text-xs text-gray-500 text-center truncate">
                        {character.anime_name}
                      </p>
                    </div>
                  );
                })}
              </div>

              {filteredCharacters.length === 0 && (
                <div className="text-center py-8 md:py-12">
                  <i className="ri-search-line text-5xl md:text-6xl text-gray-300 mb-4"></i>
                  <p className="text-xs md:text-sm text-gray-500">No characters found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
